import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';
import { RamhCredit } from '../components/RamhCredit';

interface AssocBySlug {
  id: string; name: string; type: string; slug: string;
  logo: string | null; coverImage: string | null; themeColor: string | null;
  city: string | null; district: string | null; address: string | null;
  totalUnits: number; status: string;
  company: { id: string; name: string; logo: string | null };
}

interface Dashboard {
  association: AssocBySlug & {
    bankName: string | null; bankIban: string | null;
    presidentId: string | null;
    managers: Array<{ manager: { user: { name: string; image: string | null; phone: string | null } } }>;
  };
  stats: { members: number; units: number; openTickets: number; pendingInvoices: number; pendingAmount: number };
  finance: { currentBalance: number; reserveFund: number; avgMonthlyExpense: number; avgMonthlyCollection: number };
  announcements: Array<{ id: string; title: string; content: string; priority: string; pinned: boolean; publishedAt: string }>;
  upcomingAssemblies: Array<{ id: string; title: string; scheduledAt: string }>;
  recentBills: Array<{ id: string; vendor: string; totalAmount: number | string; issueDate: string; status: string }>;
}

export function AssociationPage() {
  const { slug } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<'home' | 'fees' | 'tickets' | 'documents' | 'members'>('home');

  // 1) جلب الجمعية بالـ slug
  const slugQuery = useQuery({
    queryKey: ['assoc-slug', slug],
    queryFn: () => api.get<ApiSuccess<AssocBySlug>>(`/api/associations/by-slug/${slug}`),
    enabled: !!slug,
  });

  // 2) جلب الداشبورد بعد ما نعرف الـ id
  const id = slugQuery.data?.data?.id;
  const dash = useQuery({
    queryKey: ['assoc-dashboard', id],
    queryFn: () => api.get<ApiSuccess<Dashboard>>(`/api/associations/${id}/dashboard`),
    enabled: !!id,
  });

  if (slugQuery.isLoading) return <div className="p-8 text-gray-500">⟳ جاري التحميل…</div>;
  if (!slugQuery.data?.data) return <div className="p-8 text-center"><h2 className="text-xl font-black">الجمعية غير موجودة</h2></div>;

  const a = slugQuery.data.data;
  const d = dash.data?.data;
  const themeColor = a.themeColor ?? '#1e3a5f';

  return (
    <div className="theme-mullak min-h-screen flex flex-col" style={{ background: 'var(--surface-warm)' }}>
      {/* 🎨 Hero مدمج وأنيق */}
      <header className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)` }}>
        {a.coverImage && <img src={a.coverImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-5 flex-wrap">
            {a.logo
              ? <img src={a.logo} alt={a.name} className="w-20 h-20 rounded-2xl border-4 border-white object-cover shadow-2xl" />
              : <div className="w-20 h-20 rounded-2xl border-4 border-white shadow-2xl flex items-center justify-center text-4xl bg-white">🏛️</div>
            }
            <div className="text-white flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-black drop-shadow">{a.name}</h1>
              <p className="text-sm opacity-90 drop-shadow flex items-center gap-1">
                {a.city && <span>📍 {a.city}</span>}
                {a.district && <span>· {a.district}</span>}
              </p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center text-[11px] font-bold rounded-full px-3 py-1 bg-white/95 text-navy-dark">
                  {a.status === 'ACTIVE' ? '✓ فعّالة' : a.status === 'DRAFT' ? '📝 مسودة' : a.status}
                </span>
                <span className="inline-flex items-center text-[11px] font-bold rounded-full px-3 py-1 bg-black/30 backdrop-blur text-white border border-white/20">
                  🏢 {a.company.name}
                </span>
                <span className="inline-flex items-center text-[11px] font-bold rounded-full px-3 py-1 bg-black/30 backdrop-blur text-white border border-white/20">
                  🏠 {a.totalUnits} وحدة
                </span>
              </div>
            </div>
            <Link to="/" className="hidden md:block">
              <Button variant="ghost" className="!text-white !border-white/30 hover:!bg-white/10">← الموقع الرئيسي</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* 📑 Tabs */}
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          <NavTab active={tab === 'home'}      onClick={() => setTab('home')} themeColor={themeColor}>🏠 الرئيسية</NavTab>
          <NavTab active={tab === 'fees'}      onClick={() => setTab('fees')} themeColor={themeColor}>💰 رسومي</NavTab>
          <NavTab active={tab === 'tickets'}   onClick={() => setTab('tickets')} themeColor={themeColor}>🛠️ الصيانة</NavTab>
          <NavTab active={tab === 'members'}   onClick={() => setTab('members')} themeColor={themeColor}>👥 الأعضاء</NavTab>
          <NavTab active={tab === 'documents'} onClick={() => setTab('documents')} themeColor={themeColor}>📁 الوثائق</NavTab>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        {!d ? (
          <div className="text-center text-gray-500 py-20"><div className="animate-spin text-3xl mb-2">⟳</div>جاري التحميل…</div>
        ) : (
          <>
            {tab === 'home'      && <HomeTab d={d} themeColor={themeColor} />}
            {tab === 'fees'      && <FeesTab associationId={a.id} />}
            {tab === 'tickets'   && <TicketsTab associationId={a.id} />}
            {tab === 'members'   && <MembersTab associationId={a.id} />}
            {tab === 'documents' && <DocumentsTab associationId={a.id} />}
          </>
        )}
      </main>

      {/* 🏢 footer */}
      <footer className="py-6 border-t bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-2">
          <div className="text-xs text-gray-500">
            تُدار بواسطة <b>{a.company.name}</b> — منصة اتحاد الملاك الرقمية
          </div>
          <RamhCredit />
        </div>
      </footer>
    </div>
  );
}

function NavTab({ children, active, onClick, themeColor }: { children: React.ReactNode; active: boolean; onClick: () => void; themeColor: string }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-4 text-sm font-bold transition border-b-[3px] whitespace-nowrap ${
        active ? 'text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark hover:bg-gray-50'
      }`}
      style={active ? { borderBottomColor: themeColor, color: themeColor } : {}}
    >
      {children}
    </button>
  );
}

function EmptyState({ icon, title, hint, action }: { icon: string; title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <div className="text-center py-12 px-4">
          <div className="text-6xl mb-3 opacity-50">{icon}</div>
          <div className="font-bold text-navy-dark mb-1">{title}</div>
          {hint && <div className="text-sm text-gray-500 mb-4">{hint}</div>}
          {action}
        </div>
      </CardBody>
    </Card>
  );
}

function HomeTab({ d, themeColor }: { d: Dashboard; themeColor: string }) {
  return (
    <div className="space-y-6">
      {/* 📊 KPIs */}
      <div>
        <h2 className="text-lg font-black text-navy-dark mb-3">📊 ملخص مالي</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FancyKPI title="رصيد الجمعية" value={d.finance.currentBalance.toLocaleString('en-US')} unit="ر.س" icon="💰" themeColor={themeColor} />
          <FancyKPI title="احتياطي الصيانة" value={d.finance.reserveFund.toLocaleString('en-US')} unit="ر.س" icon="🛡️" themeColor={themeColor} />
          <FancyKPI title="متوسط الفواتير/شهر" value={d.finance.avgMonthlyExpense.toFixed(0)} unit="ر.س" icon="📊" themeColor={themeColor} />
          <FancyKPI title="متوسط التحصيل/شهر" value={d.finance.avgMonthlyCollection.toFixed(0)} unit="ر.س" icon="📈" themeColor={themeColor} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

      {/* 📰 الأخبار */}
      <div className="lg:col-span-2 space-y-3">
        <h2 className="text-xl font-black text-navy-dark">📰 آخر الأخبار</h2>
        {d.announcements.length === 0 ? (
          <Card><CardBody><div className="text-center py-8 text-gray-500">لا توجد أخبار</div></CardBody></Card>
        ) : d.announcements.map((a) => (
          <Card key={a.id} className={a.priority === 'URGENT' ? 'border-2 border-red-300' : a.pinned ? 'border-2 border-gold' : ''}>
            <CardBody>
              <div className="flex items-start gap-2 mb-2">
                {a.pinned && <span className="text-gold">📌</span>}
                <Badge color={a.priority === 'URGENT' ? 'red' : a.priority === 'IMPORTANT' ? 'amber' : 'navy'}>
                  {a.priority === 'URGENT' ? 'عاجل' : a.priority === 'IMPORTANT' ? 'مهم' : 'إعلان'}
                </Badge>
                <span className="text-xs text-gray-500 mr-auto">{new Date(a.publishedAt).toLocaleDateString('ar-SA')}</span>
              </div>
              <h3 className="font-black text-navy-dark mb-1">{a.title}</h3>
              <p className="text-sm text-gray-700 whitespace-pre-line line-clamp-3">{a.content}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* الجانب الأيمن */}
      <aside className="space-y-4">
        {/* 👨‍💼 المديرون */}
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">👨‍💼 إدارة الجمعية</h3>
            {d.association.managers.length === 0 ? <div className="text-sm text-gray-500">لم يُعيّن مدير بعد</div> : d.association.managers.map((m, i) => (
              <div key={i} className="flex items-center gap-3 mb-2">
                {m.manager.user.image ? <img src={m.manager.user.image} className="w-10 h-10 rounded-full" /> : <div className="w-10 h-10 rounded-full bg-navy-dark text-white flex items-center justify-center">👨‍💼</div>}
                <div className="flex-1">
                  <div className="font-bold text-sm">{m.manager.user.name}</div>
                  {m.manager.user.phone && <a href={`tel:${m.manager.user.phone}`} className="text-xs text-navy hover:underline" dir="ltr">{m.manager.user.phone}</a>}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>

        {/* 🗳️ جمعيات قادمة */}
        {d.upcomingAssemblies.length > 0 && (
          <Card>
            <CardBody>
              <h3 className="font-black text-navy-dark mb-3">🗳️ جمعيات قادمة</h3>
              <ul className="space-y-2 text-sm">
                {d.upcomingAssemblies.map((a) => (
                  <li key={a.id} className="flex justify-between"><span>{a.title}</span><b>{new Date(a.scheduledAt).toLocaleDateString('ar-SA')}</b></li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* 🧾 آخر الفواتير */}
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🧾 آخر فواتير الموردين</h3>
            {d.recentBills.length === 0 ? <div className="text-sm text-gray-500">لا توجد فواتير</div> : (
              <ul className="space-y-2 text-sm">
                {d.recentBills.map((b) => (
                  <li key={b.id} className="flex justify-between border-b border-gray-100 pb-1">
                    <div>
                      <div className="font-bold">{b.vendor}</div>
                      <div className="text-xs text-gray-500">{new Date(b.issueDate).toLocaleDateString('ar-SA')}</div>
                    </div>
                    <b>{Number(b.totalAmount).toLocaleString('en-US')} ر.س</b>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </aside>
      </div>
    </div>
  );
}

function FancyKPI({ title, value, unit, icon, themeColor }: { title: string; value: string; unit: string; icon: string; themeColor: string }) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">{title}</div>
            <div className="text-xl md:text-2xl font-black" style={{ color: themeColor }}>{value}</div>
            <div className="text-xs text-gray-500">{unit}</div>
          </div>
          <div className="text-3xl opacity-50">{icon}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function FeesTab({ associationId }: { associationId: string }) {
  const { data } = useQuery({
    queryKey: ['my-invoices', associationId],
    queryFn: () => api.get<{ data: Array<{ id: string; invoiceNumber: string; periodLabel: string; amount: number | string; amountPaid: number | string; status: string; dueDate: string; fee: { name: string }; unit: { unitNumber: string } | null }> }>(`/api/associations/${associationId}/invoices`),
  });
  const items = data?.data ?? [];
  return (
    <div>
      <h2 className="text-xl font-black text-navy-dark mb-4">💰 رسومي وفواتيري</h2>
      {items.length === 0 ? <EmptyState icon="💰" title="لا توجد فواتير حالياً" hint="فواتيرك المستحقة ستظهر هنا" /> : (
        <Card><CardBody className="!p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b"><tr>
              <th className="text-right px-3 py-2">رقم الفاتورة</th><th className="text-right px-3 py-2">الفترة</th><th className="text-right px-3 py-2">الوحدة</th><th className="text-right px-3 py-2">المبلغ</th><th className="text-right px-3 py-2">الاستحقاق</th><th className="text-right px-3 py-2">الحالة</th>
            </tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b">
                  <td className="px-3 py-2 font-mono text-xs" dir="ltr">{i.invoiceNumber}</td>
                  <td className="px-3 py-2">{i.periodLabel}</td>
                  <td className="px-3 py-2">{i.unit?.unitNumber ?? '—'}</td>
                  <td className="px-3 py-2 font-bold">{Number(i.amount).toLocaleString('en-US')} ر.س</td>
                  <td className="px-3 py-2 text-xs">{new Date(i.dueDate).toLocaleDateString('ar-SA')}</td>
                  <td className="px-3 py-2"><Badge color={i.status === 'PAID' ? 'green' : i.status === 'OVERDUE' ? 'red' : 'amber'}>{i.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody></Card>
      )}
    </div>
  );
}

function TicketsTab({ associationId }: { associationId: string }) {
  const qc = useQueryClient();
  const [openCreate, setOpenCreate] = useState(false);
  const { data } = useQuery({
    queryKey: ['assoc-tickets', associationId],
    queryFn: () => api.get<{ data: Array<{ id: string; ticketNumber: string; title: string; category: string; urgency: string; status: string; createdAt: string; assignedTechnician: string | null }> }>(`/api/associations/${associationId}/tickets`),
  });
  const items = data?.data ?? [];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-black text-navy-dark">🛠️ طلبات الصيانة</h2>
        <Button variant="primary" onClick={() => setOpenCreate(true)}>+ طلب صيانة</Button>
      </div>
      {items.length === 0 ? (
        <EmptyState icon="🛠️" title="لا توجد طلبات صيانة" hint="افتح أول طلب لك من زر '+ طلب صيانة' أعلاه" />
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <Card key={t.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-xs text-gray-500" dir="ltr">{t.ticketNumber}</span>
                      <Badge color={t.urgency === 'EMERGENCY' ? 'red' : t.urgency === 'HIGH' ? 'amber' : 'navy'}>{t.urgency}</Badge>
                      <Badge color={t.status === 'COMPLETED' ? 'green' : 'amber'}>{t.status}</Badge>
                    </div>
                    <h3 className="font-black text-navy-dark">{t.title}</h3>
                    {t.assignedTechnician && <p className="text-xs text-gray-500 mt-1">👷 التقني: {t.assignedTechnician}</p>}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      {openCreate && <CreateTicketModal associationId={associationId} onClose={() => setOpenCreate(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['assoc-tickets', associationId] })} />}
    </div>
  );
}

function CreateTicketModal({ associationId, onClose, onSaved }: { associationId: string; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ title: '', description: '', category: 'OTHER', urgency: 'NORMAL', location: '' });
  const m = useMutation({
    mutationFn: () => api.post(`/api/associations/${associationId}/tickets`, f),
    onSuccess: () => { onSaved(); onClose(); },
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full mx-auto my-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-xl font-black text-navy-dark">🛠️ طلب صيانة جديد</h2>
          <button onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <Input label="عنوان المشكلة *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="مثلاً: عطل في مصعد المبنى أ" />
          <div>
            <label className="block text-sm font-bold text-ink mb-1">الوصف *</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={4} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الفئة</label>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                <option value="PLUMBING">سباكة</option>
                <option value="ELECTRICITY">كهرباء</option>
                <option value="AC_HVAC">تكييف</option>
                <option value="ELEVATOR">مصعد</option>
                <option value="CLEANING">نظافة</option>
                <option value="SECURITY">أمن</option>
                <option value="GARDENING">حدائق</option>
                <option value="GENERATOR">مولّد</option>
                <option value="WATER_TANK">خزان</option>
                <option value="COMMON_AREA">منطقة مشتركة</option>
                <option value="OTHER">أخرى</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-ink mb-1">الأهمية</label>
              <select value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                <option value="LOW">منخفضة</option>
                <option value="NORMAL">عادية</option>
                <option value="HIGH">مرتفعة</option>
                <option value="EMERGENCY">طوارئ</option>
              </select>
            </div>
          </div>
          <Input label="المكان (اختياري)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="السطح، خلف خزان 2" />
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button variant="primary" onClick={() => m.mutate()} loading={m.isPending} disabled={!f.title || !f.description}>إرسال الطلب</Button>
        </div>
      </div>
    </div>
  );
}

function MembersTab({ associationId }: { associationId: string }) {
  const { data } = useQuery({
    queryKey: ['assoc-members', associationId],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; fullName: string; role: string; committeeRole: string; phone: string | null; unit: { unitNumber: string } | null }>>>(`/api/associations/${associationId}/members`),
  });
  const items = data?.data ?? [];
  return (
    <div>
      <h2 className="text-xl font-black text-navy-dark mb-4">👥 الأعضاء</h2>
      {items.length === 0 ? (
        <EmptyState icon="👥" title="لا يوجد أعضاء بعد" hint="سيظهر الأعضاء هنا بعد تسجيلهم من قبل مدير العقار" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((m) => (
            <Card key={m.id}><CardBody>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy-dark text-white flex items-center justify-center">👤</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{m.fullName}</div>
                  <div className="text-xs text-gray-500">{m.unit?.unitNumber ? `وحدة ${m.unit.unitNumber}` : '—'}</div>
                  {m.committeeRole !== 'NONE' && <Badge color="gold">{m.committeeRole}</Badge>}
                </div>
              </div>
            </CardBody></Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ associationId }: { associationId: string }) {
  const { data } = useQuery({
    queryKey: ['assoc-docs', associationId],
    queryFn: () => api.get<ApiSuccess<Array<{ id: string; title: string; category: string; fileUrl: string }>>>(`/api/associations/${associationId}/documents`),
  });
  const items = data?.data ?? [];
  return (
    <div>
      <h2 className="text-xl font-black text-navy-dark mb-4">📁 وثائق الجمعية</h2>
      {items.length === 0 ? (
        <EmptyState icon="📁" title="لا توجد وثائق" hint="الصكوك واللوائح والعقود ستظهر هنا" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((d) => (
            <Card key={d.id}><CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold">{d.title}</div>
                  <Badge color="navy">{d.category}</Badge>
                </div>
                <a href={d.fileUrl} target="_blank" rel="noreferrer" className="text-navy hover:underline">📎 عرض</a>
              </div>
            </CardBody></Card>
          ))}
        </div>
      )}
    </div>
  );
}
